'use client'

import { fetchBackendEvents, fetchBackendMarkets } from '@/lib/api/backend'
import type { BackendEventsParams } from '@/lib/api/backend-types'
import type { SortKey } from '@/lib/api/params'
import { MergedEventStream, MergedMarketStream } from '@/lib/merge-stream'
import type { FilterConfig, SortConfig } from '@/lib/merge-stream/types'
import {
	DEFAULT_FILTERS,
	DEFAULT_SORT,
	hasActiveFilters,
	hasLegacyPlatforms,
} from '@/lib/merge-stream/types'
import type { EventListItem, MarketListItem, Platform } from '@/types/market'
import { create } from 'zustand'

const BATCH_SIZE = 20

const BACKEND_PLATFORMS: Platform[] = ['polymarket', 'kalshi', 'predictfun']

// Resolve the `source` param for the backend API by only considering backend-supported platforms
function resolveBackendSource(platforms: Platform[]): string {
	const backendOnly = platforms.filter(p => BACKEND_PLATFORMS.includes(p))
	if (backendOnly.length === 0 || backendOnly.length === BACKEND_PLATFORMS.length) return 'all'
	return backendOnly.join(',')
}

// Helper to check if an event has ended (endDate is unix seconds)
function isEventEnded(endDate: number | undefined): boolean {
	if (!endDate) return false
	return endDate * 1000 < Date.now()
}

// Helper to check if a market has ended (closeTime is unix ms) - unused, kept for filterNewMarkets
// function isMarketEnded(closeTime: number): boolean {
// 	return closeTime < Date.now()
// }

// Helper to filter and deduplicate events
function filterNewEvents(
	newEvents: EventListItem[],
	existingEvents: EventListItem[],
	filters: FilterConfig
): EventListItem[] {
	const existingIds = new Set(existingEvents.map(e => `${e.platform}:${e.id}`))
	const activeOnly = filters.activeOnly !== false

	return newEvents.filter(e => {
		if (existingIds.has(`${e.platform}:${e.id}`)) return false
		if (activeOnly && (e.isInactive || isEventEnded(e.endDate))) return false
		return true
	})
}

// Helper to filter and deduplicate markets (unused - kept for future use)
// function filterNewMarkets(
// 	newMarkets: MarketListItem[],
// 	existingMarkets: MarketListItem[],
// 	filters: FilterConfig
// ): MarketListItem[] {
// 	const existingIds = new Set(existingMarkets.map(m => `${m.platform}:${m.id}`))
// 	const activeOnly = filters.activeOnly !== false
//
// 	return newMarkets.filter(m => {
// 		if (existingIds.has(`${m.platform}:${m.id}`)) return false
// 		if (activeOnly && (m.status !== 'active' || isMarketEnded(m.closeTime))) return false
// 		return true
// 	})
// }

// Abort controllers for cancelling in-flight requests
let eventsAbortController: AbortController | null = null
const marketsAbortController: AbortController | null = null

type MarketsState = {
	// Events data
	events: EventListItem[]
	eventsLoading: boolean
	eventsHasMore: boolean
	eventsError: Error | null
	eventsSort: SortConfig
	eventsFilters: FilterConfig

	// Markets data
	markets: MarketListItem[]
	marketsLoading: boolean
	marketsHasMore: boolean
	marketsError: Error | null
	marketsSort: SortConfig
	marketsFilters: FilterConfig

	// Streams (kept in memory, internal use)
	eventStream: MergedEventStream | null
	marketStream: MergedMarketStream | null

	// Backend mode state for events
	useBackendMode: boolean
	backendOffset: number

	// Backend mode state for markets
	useMarketsBackendMode: boolean
	backendMarketsOffset: number

	// Actions
	loadMoreEvents: (filters?: FilterConfig) => Promise<void>
	loadMoreMarkets: () => Promise<void>
	setEventsSort: (sort: SortConfig) => void
	setMarketsSort: (sort: SortConfig) => void
	setEventsFilters: (filters: FilterConfig) => void
	setMarketsFilters: (filters: FilterConfig) => void
	resetEvents: () => void
	resetMarkets: () => void
	refreshEvents: (sort: SortConfig, filters: FilterConfig) => void
	prefetch: () => Promise<void>
}

export const useMarketsStore = create<MarketsState>((set, get) => ({
	// Initial state - events
	events: [],
	eventsLoading: false,
	eventsHasMore: true,
	eventsError: null,
	eventsSort: DEFAULT_SORT,
	eventsFilters: DEFAULT_FILTERS,

	// Initial state - markets
	markets: [],
	marketsLoading: false,
	marketsHasMore: true,
	marketsError: null,
	marketsSort: DEFAULT_SORT,
	marketsFilters: DEFAULT_FILTERS,

	// Streams
	eventStream: null,
	marketStream: null,

	// Backend mode for events
	useBackendMode: false,
	backendOffset: 0,

	// Backend mode for markets
	useMarketsBackendMode: false,
	backendMarketsOffset: 0,

	loadMoreEvents: async (overrideFilters?: FilterConfig) => {
		const state = get()
		if (state.eventsLoading) {
			return
		}

		// Cancel any previous request and create new abort controller
		if (eventsAbortController) {
			eventsAbortController.abort()
		}
		eventsAbortController = new AbortController()
		// Store local reference to this request's controller
		const controller = eventsAbortController

		set({ eventsLoading: true, eventsError: null })

		try {
			// Re-fetch state after set() to get the latest useBackendMode and other values
			const currentState = get()

			if (currentState.useBackendMode) {
				// Fetch from backend - use overrideFilters if provided (from refreshEvents)
				const filters = overrideFilters ?? currentState.eventsFilters
				const platforms = filters.platforms || ['kalshi', 'polymarket']

				// Map sort key to backend format
				const sortMap: Record<SortKey, BackendEventsParams['sort']> = {
					volume: 'volume',
					volume24h: 'volume',
					liquidity: 'liquidity',
					openInterest: 'volume',
					startDate: 'end_date',
				}

				// Backend expects capitalized categories (Politics, Crypto, etc)
				const capitalizeCategory = (c: string) => c.charAt(0).toUpperCase() + c.slice(1)

				const params: BackendEventsParams = {
					q: filters.search,
					category: filters.categories?.map(capitalizeCategory).join(','),
					source: resolveBackendSource(platforms),
					status: filters.activeOnly === false ? 'all' : 'active',
					sort: sortMap[currentState.eventsSort.key],
					order: 'desc',
					limit: BATCH_SIZE,
					offset: currentState.backendOffset,
					expiring_soon: filters.expiringSoon || undefined,
					closing_time: filters.closingTime,
					min_probability: filters.minProbability,
					min_volume: filters.minVolume,
					max_volume: filters.maxVolume,
					min_liquidity: filters.minLiquidity,
					max_liquidity: filters.maxLiquidity,
				}

				// Fetch backend + legacy platforms in parallel
				const includePredictFun =
					filters.search &&
					state.backendOffset === 0 &&
					(!filters.platforms ||
						filters.platforms.length === 0 ||
						filters.platforms.includes('predictfun'))

				const includeMyriad =
					currentState.backendOffset === 0 &&
					(!filters.platforms ||
						filters.platforms.length === 0 ||
						filters.platforms.includes('myriad'))

				const includeLimitless =
					currentState.backendOffset === 0 &&
					(!filters.platforms ||
						filters.platforms.length === 0 ||
						filters.platforms.includes('limitless'))

				const includeHyperliquid =
					currentState.backendOffset === 0 &&
					(!filters.platforms ||
						filters.platforms.length === 0 ||
						filters.platforms.includes('hyperliquid'))

				const myriadParams = new URLSearchParams()
				myriadParams.set('limit', '50')
				myriadParams.set('offset', '0')
				myriadParams.set('status', filters.activeOnly === false ? 'all' : 'active')
				if (filters.categories?.length) {
					myriadParams.set('categories', filters.categories.join(','))
				}
				if (filters.search) {
					myriadParams.set('search', filters.search)
				}

				const limitlessParams = new URLSearchParams()
				limitlessParams.set('limit', '25')
				if (filters.search) {
					// `/api/limitless/events` reads via parseListParams which expects
					// `search`, not `query`. The mismatch silently dropped the search
					// term and fell into the "no filter" branch — searching for
					// e.g. "apple" returned 25 random crypto markets instead of the
					// matching AAPL stock prediction markets.
					limitlessParams.set('search', filters.search)
				}
				if (filters.categories?.length) {
					limitlessParams.set('categories', filters.categories.join(','))
				}

				const hyperliquidParams = new URLSearchParams()
				hyperliquidParams.set('limit', '50')
				hyperliquidParams.set('status', filters.activeOnly === false ? 'all' : 'active')
				if (filters.search) {
					hyperliquidParams.set('search', filters.search)
				}

				// Skip the backend fetch entirely when the user has explicitly
				// picked only non-backend platforms (e.g. just Hyperliquid, or
				// just Myriad). resolveBackendSource() collapses that case to
				// 'all', which would make the backend stream every event from
				// Polymarket/Kalshi/PredictFun and drive infinite-scroll into
				// a 1500-events runaway even though the user didn't ask for
				// those platforms. Distinguishing "no filter" (default → fetch
				// all) from "only non-backend chosen" (skip backend) is the
				// only correct behaviour here.
				const userPlatforms = filters.platforms
				const skipBackend =
					Array.isArray(userPlatforms) &&
					userPlatforms.length > 0 &&
					!userPlatforms.some(p => BACKEND_PLATFORMS.includes(p))

				const [result, predictFunEvents, myriadEvents, limitlessEvents, hyperliquidEvents] =
					await Promise.all([
						skipBackend
							? Promise.resolve({
									data: [] as EventListItem[],
									meta: { hasMore: false },
								})
							: fetchBackendEvents(params, controller.signal),
						includePredictFun
							? fetch(
									`/api/predictfun/events?search=${encodeURIComponent(filters.search ?? '')}&limit=20&status=active`,
									{ signal: controller.signal }
								)
									.then(r => (r.ok ? r.json() : { data: [] }))
									.then(r => r.data as EventListItem[])
									.catch(() => [] as EventListItem[])
							: Promise.resolve([] as EventListItem[]),
						includeMyriad
							? fetch(`/api/myriad/events?${myriadParams.toString()}`, {
									signal: controller.signal,
								})
									.then(r => (r.ok ? r.json() : { data: [] }))
									.then(r => r.data as EventListItem[])
									.catch(() => [] as EventListItem[])
							: Promise.resolve([] as EventListItem[]),
						includeLimitless
							? fetch(`/api/limitless/events?${limitlessParams.toString()}`, {
									signal: controller.signal,
								})
									.then(r => (r.ok ? r.json() : { data: [] }))
									.then(r => r.data as EventListItem[])
									.catch(() => [] as EventListItem[])
							: Promise.resolve([] as EventListItem[]),
						includeHyperliquid
							? fetch(`/api/hyperliquid/events?${hyperliquidParams.toString()}`, {
									signal: controller.signal,
								})
									.then(r => (r.ok ? r.json() : { data: [] }))
									.then(r => r.data as EventListItem[])
									.catch(() => [] as EventListItem[])
							: Promise.resolve([] as EventListItem[]),
					])

				const allEvents = [
					...result.data,
					...predictFunEvents,
					...myriadEvents,
					...limitlessEvents,
					...hyperliquidEvents,
				]

				// Only update state if this request wasn't aborted
				if (!controller.signal.aborted) {
					set(s => ({
						events: [...s.events, ...filterNewEvents(allEvents, s.events, filters)],
						eventsHasMore: result.meta.hasMore,
						eventsLoading: false,
						backendOffset: s.backendOffset + result.data.length,
					}))
				}
			} else {
				// Use MergedStream (existing logic)
				// Use overrideFilters if provided (from refreshEvents)
				const filters = overrideFilters ?? currentState.eventsFilters
				let stream = currentState.eventStream
				if (!stream) {
					stream = new MergedEventStream(currentState.eventsSort, filters)
					set({ eventStream: stream })
				}

				const newEvents = await stream.getNext(BATCH_SIZE)

				// Only update state if this request wasn't aborted
				if (!controller.signal.aborted) {
					set(s => ({
						events: [...s.events, ...filterNewEvents(newEvents, s.events, filters)],
						eventsHasMore: stream?.hasMore() ?? false,
						eventsLoading: false,
					}))
				}
			}
		} catch (err) {
			// Don't update error state if request was aborted
			if (err instanceof Error && err.name === 'AbortError') {
				return
			}
			console.error('Error loading events:', err)
			set({
				eventsError: err instanceof Error ? err : new Error('Failed to load events'),
				eventsLoading: false,
			})
		} finally {
			// Clean up abort controller if it's still ours
			if (eventsAbortController === controller && !controller.signal.aborted) {
				eventsAbortController = null
			}
		}
	},

	loadMoreMarkets: async () => {
		const state = get()
		if (state.marketsLoading) {
			return
		}

		set({ marketsLoading: true, marketsError: null })

		try {
			// Re-fetch state after set() to get the latest useMarketsBackendMode and other values
			const currentState = get()

			if (currentState.useMarketsBackendMode) {
				// Fetch from backend
				const filters = currentState.marketsFilters
				const platforms = filters.platforms || ['kalshi', 'polymarket']

				// Map sort key to backend format
				const sortMap: Record<SortKey, BackendEventsParams['sort']> = {
					volume: 'volume',
					volume24h: 'volume',
					liquidity: 'liquidity',
					openInterest: 'volume',
					startDate: 'end_date',
				}

				// Backend expects capitalized categories (Politics, Crypto, etc)
				const capitalizeCategory = (c: string) => c.charAt(0).toUpperCase() + c.slice(1)

				const params: BackendEventsParams = {
					q: filters.search,
					category: filters.categories?.map(capitalizeCategory).join(','),
					source: resolveBackendSource(platforms),
					status: filters.activeOnly === false ? 'all' : 'active',
					sort: sortMap[currentState.marketsSort.key],
					order: 'desc',
					limit: BATCH_SIZE,
					offset: currentState.backendMarketsOffset,
					expiring_soon: filters.expiringSoon || undefined,
					closing_time: filters.closingTime,
					min_probability: filters.minProbability,
					min_volume: filters.minVolume,
					max_volume: filters.maxVolume,
					min_liquidity: filters.minLiquidity,
					max_liquidity: filters.maxLiquidity,
				}

				const result = await fetchBackendMarkets(params)

				set(s => {
					const existingIds = new Set(s.markets.map(m => `${m.platform}:${m.id}`))
					const uniqueNewMarkets = result.data.filter(
						m => !existingIds.has(`${m.platform}:${m.id}`)
					)
					return {
						markets: [...s.markets, ...uniqueNewMarkets],
						marketsHasMore: result.meta.hasMore,
						marketsLoading: false,
						backendMarketsOffset: s.backendMarketsOffset + result.data.length,
					}
				})
			} else {
				// Use MergedStream (existing logic)
				let stream = currentState.marketStream
				if (!stream) {
					stream = new MergedMarketStream(currentState.marketsSort, currentState.marketsFilters)
					set({ marketStream: stream })
				}

				const newMarkets = await stream.getNext(BATCH_SIZE)

				set(s => {
					// Deduplicate in case of race conditions
					const existingIds = new Set(s.markets.map(m => `${m.platform}:${m.id}`))
					const uniqueNewMarkets = newMarkets.filter(m => !existingIds.has(`${m.platform}:${m.id}`))
					return {
						markets: [...s.markets, ...uniqueNewMarkets],
						marketsHasMore: stream?.hasMore() ?? false,
						marketsLoading: false,
					}
				})
			}
		} catch (err) {
			console.error('Error loading markets:', err)
			set({
				marketsError: err instanceof Error ? err : new Error('Failed to load markets'),
				marketsLoading: false,
			})
		}
	},

	setEventsSort: (sort: SortConfig) => {
		const state = get()
		if (sort.key === state.eventsSort.key && sort.direction === state.eventsSort.direction) {
			return
		}

		// Cancel any in-flight request
		if (eventsAbortController) {
			eventsAbortController.abort()
			eventsAbortController = null
		}

		if (state.useBackendMode) {
			// Keep backend mode, just reset
			set({
				eventsSort: sort,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				backendOffset: 0,
				eventsLoading: false,
			})
		} else {
			// Create new stream
			const stream = new MergedEventStream(sort, state.eventsFilters)
			set({
				eventsSort: sort,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
			})
		}

		get().loadMoreEvents()
	},

	setMarketsSort: (sort: SortConfig) => {
		const state = get()
		if (sort.key === state.marketsSort.key && sort.direction === state.marketsSort.direction) {
			return
		}

		if (state.useMarketsBackendMode) {
			// Keep backend mode, just reset
			set({
				marketsSort: sort,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				backendMarketsOffset: 0,
			})
		} else {
			// Create new stream
			const stream = new MergedMarketStream(sort, state.marketsFilters)
			set({
				marketsSort: sort,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
			})
		}

		// Immediately fetch first batch
		get().loadMoreMarkets()
	},

	setEventsFilters: (filters: FilterConfig) => {
		const state = get()
		const platforms = filters.platforms || [
			'kalshi',
			'polymarket',
			'opinionlabs',
			'predictfun',
			'limitless',
			'myriad',
		]
		const hasBackendPlatforms = platforms.some(p => BACKEND_PLATFORMS.includes(p))
		const shouldUseBackend =
			!hasLegacyPlatforms(platforms) || (hasActiveFilters(filters) && hasBackendPlatforms)

		// Cancel any in-flight request
		if (eventsAbortController) {
			eventsAbortController.abort()
			eventsAbortController = null
		}

		if (shouldUseBackend) {
			// Use backend API
			set({
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: null,
				useBackendMode: true,
				backendOffset: 0,
				eventsLoading: false,
			})
		} else {
			// Use MergedStream
			const stream = new MergedEventStream(state.eventsSort, filters)
			set({
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
				useBackendMode: false,
				backendOffset: 0,
				eventsLoading: false,
			})
		}

		get().loadMoreEvents()
	},

	setMarketsFilters: (filters: FilterConfig) => {
		const state = get()
		const platforms = filters.platforms || [
			'kalshi',
			'polymarket',
			'opinionlabs',
			'predictfun',
			'limitless',
			'myriad',
		]
		const hasBackendPlatforms = platforms.some(p => BACKEND_PLATFORMS.includes(p))
		const shouldUseBackend =
			!hasLegacyPlatforms(platforms) || (hasActiveFilters(filters) && hasBackendPlatforms)

		if (shouldUseBackend) {
			// Use backend API
			set({
				marketsFilters: filters,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: null,
				useMarketsBackendMode: true,
				backendMarketsOffset: 0,
			})
		} else {
			// Use MergedStream
			const stream = new MergedMarketStream(state.marketsSort, filters)
			set({
				marketsFilters: filters,
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
				useMarketsBackendMode: false,
				backendMarketsOffset: 0,
			})
		}

		get().loadMoreMarkets()
	},

	resetEvents: () => {
		const state = get()

		if (state.useBackendMode) {
			set({
				events: [],
				eventsHasMore: true,
				eventsError: null,
				backendOffset: 0,
			})
		} else {
			const stream = new MergedEventStream(state.eventsSort, state.eventsFilters)
			set({
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
			})
		}

		get().loadMoreEvents()
	},

	refreshEvents: (sort: SortConfig, filters: FilterConfig) => {
		const platforms = filters.platforms || [
			'kalshi',
			'polymarket',
			'opinionlabs',
			'predictfun',
			'limitless',
			'myriad',
		]
		const hasBackendPlatforms = platforms.some(p => BACKEND_PLATFORMS.includes(p))
		const shouldUseBackend =
			!hasLegacyPlatforms(platforms) || (hasActiveFilters(filters) && hasBackendPlatforms)

		// Cancel any in-flight request
		if (eventsAbortController) {
			eventsAbortController.abort()
			eventsAbortController = null
		}

		if (shouldUseBackend) {
			set({
				eventsSort: sort,
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: null,
				useBackendMode: true,
				backendOffset: 0,
				eventsLoading: false,
			})
		} else {
			const stream = new MergedEventStream(sort, filters)
			set({
				eventsSort: sort,
				eventsFilters: filters,
				events: [],
				eventsHasMore: true,
				eventsError: null,
				eventStream: stream,
				useBackendMode: false,
				backendOffset: 0,
				eventsLoading: false,
			})
		}

		// Pass filters directly to avoid race condition with get()
		get().loadMoreEvents(filters)
	},

	resetMarkets: () => {
		const state = get()

		if (state.useMarketsBackendMode) {
			set({
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				backendMarketsOffset: 0,
			})
		} else {
			const stream = new MergedMarketStream(state.marketsSort, state.marketsFilters)
			set({
				markets: [],
				marketsHasMore: true,
				marketsError: null,
				marketStream: stream,
			})
		}
		get().loadMoreMarkets()
	},

	// Prefetch both events and markets on app start
	prefetch: async () => {
		const state = get()

		// Only prefetch if we haven't loaded anything yet
		if (state.events.length === 0 && !state.eventsLoading) {
			get().loadMoreEvents()
		}
		if (state.markets.length === 0 && !state.marketsLoading) {
			get().loadMoreMarkets()
		}
	},
}))
